/**
 * Logo source for a CUSTOM (Control Center) MCP connection.
 *
 * There is no logo field on an mcp_connection, so the logo is the vendor's own
 * favicon, derived from the connection URL. The subtlety learned in production:
 * an MCP endpoint lives on a bare protocol host (mcp.deepwiki.com,
 * mcp.context7.com — uvicorn/API servers) that returns 404 for /favicon.ico.
 * The favicon lives on the vendor's MARKETING domain (deepwiki.com,
 * context7.com). So we strip a leading service subdomain (mcp./api./…) to reach
 * the domain that actually serves an icon. Verified:
 *   mcp.deepwiki.com/favicon.ico -> 404 ,  deepwiki.com/favicon.ico -> 200
 *   mcp.context7.com/favicon.ico -> 404 ,  context7.com/favicon.ico -> 200
 *
 * Vendor-agnostic: any custom MCP gets its real mark with no code change. When
 * the derived favicon 404s or fails to load, the card's existing onError (swapToBundledMcpLogo) drops to the
 * generic mark — favicon.ico is not a .png, so it never enters the bundled-logo
 * swap loop.
 *
 * Returns undefined for anything not a plain https URL, so the caller falls
 * back to the generic mark.
 */

// Leading host labels that identify the protocol endpoint rather than the
// vendor's site. Stripping one of these reaches the domain that serves a
// favicon. Only stripped when a registrable domain (>= 2 labels) remains, so a
// multi-part TLD (mcp.example.co.uk -> example.co.uk) survives.
const SERVICE_SUBDOMAINS = new Set([
  'mcp',
  'api',
  'server',
  'gateway',
  'app',
  'www',
]);

export const customMcpLogoUrl = (url?: string | null): string | undefined => {
  if (!url || typeof url !== 'string') return undefined;

  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return undefined;
  }

  // https only: an http favicon would be mixed content and get blocked anyway.
  if (parsed.protocol !== 'https:') return undefined;
  const hostname = parsed.hostname;
  if (!hostname) return undefined;

  const labels = hostname.split('.');
  let host = hostname;
  if (labels.length >= 3 && SERVICE_SUBDOMAINS.has(labels[0].toLowerCase())) {
    host = labels.slice(1).join('.');
  }

  return `https://${host}/favicon.ico`;
};
